import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { jsonrepair } from 'jsonrepair'

import { Observable, map, scan } from 'rxjs'

import {
  AiAgent,
  Message,
  SendFeedbackPayload,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

export type AnswerPayload = {
  question: string
  session_id?: string | null
  application_id?: string
  application_version?: string
  jump_to_process_doc?: boolean
}

const SOURCES_IDENTIFIER = '{"sources":'
const FOLLOWUP_IDENTIFIER = '{"questions":'
const OBJECTS_IDENTIFIER = '{"objects":'
const DATA_IDENTIFIER = 'data: '

@Injectable({ providedIn: 'root' })
export class AiAssistantAPI extends ApiHttpService {
  #assistant = `ai-engine/agents/assistant`

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  getAnswer(payload: AnswerPayload): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.stream(`${this.#assistant}/stream`, payload).pipe(
      map((response) => {
        if (response.startsWith(DATA_IDENTIFIER)) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      scan(
        (acc, curr) =>
          `${acc}${
            curr
              .replace(/\r\n\r\n$/, '') // EOL fix
              .replaceAll(`\r\n\r\n${DATA_IDENTIFIER}`, '') // multiple lines fix
              .replaceAll(DATA_IDENTIFIER, '') // normal start identifier fix
          }`,
        ''
      ),
      map((answerStr) => {
        const { textStr, metaDataStr, followupStr, objectsStr } = this.#parseAnswerStr(answerStr)
        const metaData = this.#deserializeMetaData(metaDataStr, followupStr, objectsStr)
        return {
          message: textStr,
          ...metaData,
        }
      })
    )
  }

  getSSEAnswer(payload: AnswerPayload): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#assistant}/chat`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#assistant}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  #parseAnswerStr(answerStr: string): {
    textStr: string
    metaDataStr: string
    followupStr: string
    objectsStr: string
  } {
    const sourcesIndex = answerStr.indexOf(SOURCES_IDENTIFIER)
    const followupIndex = answerStr.indexOf(FOLLOWUP_IDENTIFIER)
    const objectsIndex = answerStr.indexOf(OBJECTS_IDENTIFIER)
    let textStr = ''
    textStr = sourcesIndex === -1 ? answerStr : answerStr.substring(0, sourcesIndex)
    if (objectsIndex !== -1 && objectsIndex < followupIndex) {
      textStr = answerStr.substring(0, objectsIndex)
    }
    const metaDataStr = sourcesIndex === -1 ? '' : answerStr.substring(sourcesIndex, followupIndex)
    const objectsStr = objectsIndex === -1 ? '' : answerStr.substring(objectsIndex, followupIndex)
    const followupStr = followupIndex === -1 ? '' : answerStr.substring(followupIndex)

    return { textStr, metaDataStr, followupStr, objectsStr }
  }

  #deserializeMetaData(
    metaDataStr: string,
    followupStr: string,
    objectsStr: string
  ): Pick<Message, 'sources' | 'prompts' | 'objects' | 'context'> | Record<string, never> {
    if (!metaDataStr && !followupStr) {
      return {}
    }

    try {
      const metaData = metaDataStr.length > 0 ? JSON.parse(jsonrepair(metaDataStr)) : {}
      const followupData = followupStr.length > 0 ? JSON.parse(jsonrepair(followupStr)) : {}
      const objectsData = objectsStr.length > 0 ? JSON.parse(jsonrepair(objectsStr)) : {}
      return {
        sources: metaData.sources,
        prompts: followupData.questions.map((question: string) => ({
          action: question,
          description: null,
          handled_by: AiAgent.ASSISTANT,
        })),
        objects: objectsData.objects,
        context: { session_id: metaData.session_id, request_id: metaData.request_id },
      }
    } catch (e) {
      console.error('Error parsing metaDataStr, followupStr, or objectsStr', e)
      return {}
    }
  }
}
